import type { PlatformerLevel } from '../CharacterTypes';
import { routeForPlatform } from './portalRoute';
import { isPortalSealed } from './portalSeal';

/**
 * The authored portal graph (lane AK, 2026-10-08): which maps a player can walk
 * to from an entry map through teleporter platforms, and the links between
 * them. One walk, two readers:
 *
 *   - the guest story-map gate (server/auth/guestMapAccess.ts storyMapsFrom)
 *     takes `maps` with the active experience's blocked edges removed;
 *   - the World Map window (game/ui/Windows/WorldMap) draws `maps` as places
 *     and `pairLinks(links)` as the dotted roads between them.
 *
 * The levels' teleporter platforms are the ground truth for "connected". A
 * sealed teleporter (portalSeal.ts) is listed as a sealed link — the World Map
 * draws that way as sealed — but never walked: it leads nowhere (plan 26 F13).
 * status/map-program.json's authored worldConnections are cross-checked against
 * this walk in a test (game/ui/Windows/WorldMap/worldMapLayout.test.ts).
 */

export type PortalLevelLookup = (mapId: string) => Pick<PlatformerLevel, 'id' | 'platforms'> | undefined;

/** One authored portal: a teleporter on `from` that leads to `to`. */
export interface PortalLink {
  from: string;
  to: string;
  portalId: string;
  /** Authored `sealed`: placed and named, but no one can travel it. */
  sealed?: boolean;
  /** Authored `concealed`: secret transition; presentation may hide or disguise the road/tag. */
  concealed?: boolean;
}

export interface PortalGraph {
  /** Reachable maps in breadth-first order from the entry (entry first). */
  maps: string[];
  /** Every allowed portal between two reachable maps, in walk order. */
  links: PortalLink[];
}

/** Optional per-edge policy, e.g. an experience's blocked natural transfers. */
export type PortalEdgeFilter = (from: string, to: string) => boolean;

/**
 * Breadth-first walk of the authored portal graph from `entryMapId`. Portals
 * to unknown maps and to the same map (rim/loop portals) are not links; an
 * edge the filter refuses is neither walked nor listed; a sealed portal is
 * listed (`sealed: true`) but not walked. A sealed link may name a map the
 * walk never reaches — readers draw it only between maps they place.
 */
export function walkPortalGraph(
  entryMapId: string,
  getLevel: PortalLevelLookup,
  allowEdge: PortalEdgeFilter = () => true,
): PortalGraph {
  const reached = new Set<string>([entryMapId]);
  const maps = [entryMapId];
  const links: PortalLink[] = [];
  for (let index = 0; index < maps.length; index++) {
    const from = maps[index];
    for (const platform of getLevel(from)?.platforms ?? []) {
      if (platform.type !== 'teleporter') continue;
      const to = routeForPlatform(platform, from).destinationMapId;
      if (to === from || !getLevel(to) || !allowEdge(from, to)) continue;
      const concealed = Boolean((platform as any).concealed || (platform as any).teleporterConfig?.concealed);
      if (isPortalSealed(platform)) {
        links.push({ from, to, portalId: platform.id, sealed: true, ...(concealed ? { concealed: true } : {}) });
        continue;
      }
      links.push({ from, to, portalId: platform.id, ...(concealed ? { concealed: true } : {}) });
      if (!reached.has(to)) {
        reached.add(to);
        maps.push(to);
      }
    }
  }
  return { maps, links };
}

/** An undirected road between two maps, as a map draws it. */
export interface PortalPair {
  /** Endpoints in first-seen order (the walk reaches `a` first). */
  a: string;
  b: string;
  /** An open portal leads a→b. */
  forward: boolean;
  /** An open portal leads b→a. */
  backward: boolean;
  /** A sealed portal leads a→b and no open one does. */
  sealedForward: boolean;
  /** A sealed portal leads b→a and no open one does. */
  sealedBackward: boolean;
  /** Authored concealed portal leads a→b. */
  concealedForward?: boolean;
  /** Authored concealed portal leads b→a. */
  concealedBackward?: boolean;
}

/** Stable key for an unordered map pair. */
export function portalPairKey(left: string, right: string): string {
  return left < right ? `${left}|${right}` : `${right}|${left}`;
}

/** Collapse directed portal links into one road per map pair. */
export function pairLinks(links: readonly PortalLink[]): PortalPair[] {
  const pairs = new Map<string, PortalPair>();
  const sealedDirs = new Set<string>();
  const concealedDirs = new Set<string>();
  for (const link of links) {
    const key = portalPairKey(link.from, link.to);
    const pair = pairs.get(key)
      ?? { a: link.from, b: link.to, forward: false, backward: false, sealedForward: false, sealedBackward: false };
    const ahead = link.from === pair.a;
    if (link.sealed) sealedDirs.add(`${key}|${ahead ? 'f' : 'b'}`);
    else if (ahead) pair.forward = true;
    else pair.backward = true;
    if (link.concealed) concealedDirs.add(`${key}|${ahead ? 'f' : 'b'}`);
    pairs.set(key, pair);
  }
  for (const [key, pair] of pairs) {
    pair.sealedForward = !pair.forward && sealedDirs.has(`${key}|f`);
    pair.sealedBackward = !pair.backward && sealedDirs.has(`${key}|b`);
    if (concealedDirs.has(`${key}|f`)) pair.concealedForward = true;
    if (concealedDirs.has(`${key}|b`)) pair.concealedBackward = true;
  }
  return [...pairs.values()];
}
